import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { OrgProvider, useOrg } from "./contexts/OrgContext";
import SubscriptionExpiredModal from "./components/SubscriptionExpiredModal";
import {
  SuperAdminProvider,
  useSuperAdmin,
} from "./contexts/SuperAdminContext";
import {
  ChannelPartnerAuthProvider,
  useChannelPartnerAuth,
} from "./contexts/ChannelPartnerAuthContext";

// Public Pages

// import Privacy from "./pages/public/pages/Privacy";
// import Terms from "./pages/public/pages/Terms";

// Admin Pages
import AdminDashboard from "./pages/Dashboard/Dashboard";
import LeadsList from "./pages/Leads/LeadsList";
import LeadDetails from "./pages/Leads/LeadDetails";
import AssignLeads from "./pages/Leads/AssignLeads";
import SmartLists from "./pages/Leads/SmartLists";
import ReEngagement from "./pages/Leads/ReEngagement";
import Territories from "./pages/Leads/Territories";
import AssignmentRules from "./pages/Settings/AssignmentRules";
import LeadsTabs from "./pages/Leads/LeadsTabs";
import Layout from "./components/Layout/Layout";
import UsersList from "./pages/Users/UsersList";
import UserDetails from "./pages/Users/UserDetails";
import CallLogs from "./pages/CallLogs/CallLogs";
import RecordingsList from "./pages/Recordings/RecordingsList";
import CallAnalysis from "./pages/CallAnalysis/CallAnalysis";
import CallAnalysisDetail from "./pages/CallAnalysis/CallAnalysisDetail";
import Reports from "./pages/Reports/Reports";
import DailyWorkReports from "./pages/Reports/DailyWorkReports";
import CEODashboard from "./pages/CEODashboard/CEODashboard";
import Analytics from "./pages/Analytics/Analytics";
import Attendance from "./pages/Attendance/Attendance";
import Settings from "./pages/Settings/Settings";
import SupportPage from "./pages/Settings/SupportPage";
import Leaderboard from "./pages/Leaderboard/Leaderboard";
import ProductsList from "./pages/Products/ProductsList";
import ScriptsList from "./pages/Scripts/ScriptsList";
import FollowUpsList from "./pages/FollowUps/FollowUpsList";
import VisitsList from "./pages/Visits/VisitsList";
import EscalatedFollowUps from "./pages/FollowUps/EscalatedFollowUps";
import AuditLogs from "./pages/AuditLogs/AuditLogs";

// User/Telecaller Pages
import UserDashboard from "./pages/User/UserDashboard";
import UserReports from "./pages/User/UserReports";
import UserCallLogs from "./pages/User/UserCallLogs";

// Auth Pages
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import AuthCallback from "./pages/Auth/AuthCallback";
import ReferralRedirect from "./pages/Auth/ReferralRedirect";
import TrackTicket from "./pages/TrackTicket";

// SuperAdmin Pages
import SuperAdminLogin from "./pages/superadmin/SuperAdminLogin";
import PartnerLogin from "./pages/partner/PartnerLogin";
import PartnerLayout from "./pages/partner/PartnerLayout";
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerOrganizations from "./pages/partner/PartnerOrganizations";
import PartnerSettings from "./pages/partner/PartnerSettings";
import PartnerHelp from "./pages/partner/PartnerHelp";
import PartnerPipeline from "./pages/partner/PartnerPipeline";
import PartnerSupport from "./pages/partner/PartnerSupport";
import PartnerScheduleMeeting from "./pages/partner/PartnerScheduleMeeting";
import SuperAdminDashboard from "./pages/superadmin/SuperAdminDashboard";
import OrganizationsList from "./pages/superadmin/OrganizationsList";
import PlansList from "./pages/superadmin/PlansList";
import AdminsList from "./pages/superadmin/AdminsList";
import SuperAdminLeadsList from "./pages/superadmin/WebsiteLeadsList";
import DemoBookingsList from "./pages/superadmin/DemoBookingsList";
import SuperAdminAuditLogs from "./pages/superadmin/AuditLogs";
import NoticesList from "./pages/superadmin/NoticesList";
import PromocodesList from "./pages/superadmin/PromocodesList";
import ChannelPartnersList from "./pages/superadmin/ChannelPartnersList";
import AppVersions from "./pages/superadmin/AppVersions";
import InvoiceSettings from "./pages/superadmin/InvoiceSettings";
import SuperAdminSettings from "./pages/superadmin/Settings";
import SupportTickets from "./pages/superadmin/SupportTickets";
import PartnerTickets from "./pages/superadmin/PartnerTickets";
import BlogPosts from "./pages/superadmin/BlogPosts";
import Videos from "./pages/superadmin/Videos";
import OtherProducts from "./pages/superadmin/OtherProducts";
import RenewalPerformanceReport from "./pages/superadmin/RenewalPerformanceReport";
import RenewalConversionAnalytics from "./pages/superadmin/RenewalConversionAnalytics";
import RenewalAgentPerformance from "./pages/superadmin/RenewalAgentPerformance";
import RenewalLossReport from "./pages/superadmin/RenewalLossReport";
import SubscriptionExpiryReport from "./pages/superadmin/SubscriptionExpiryReport";
import TicketResolutionAnalysis from "./pages/superadmin/TicketResolutionAnalysis";
import FirstResponseTimeAnalysis from "./pages/superadmin/FirstResponseTimeAnalysis";
import SlaPerformanceReport from "./pages/superadmin/SlaPerformanceReport";
import OverdueTicketReport from "./pages/superadmin/OverdueTicketReport";
import EscalationReport from "./pages/superadmin/EscalationReport";
import ReopenedTicketReport from "./pages/superadmin/ReopenedTicketReport";
import CustomerWiseTicketReport from "./pages/superadmin/CustomerWiseTicketReport";
import ProductWiseTicketReport from "./pages/superadmin/ProductWiseTicketReport";
import TicketTrendAnalysis from "./pages/superadmin/TicketTrendAnalysis";
import TicketClosureRate from "./pages/superadmin/TicketClosureRate";
import Checkout from "./pages/Checkout";
import ReferAndEarn from "./pages/ReferAndEarn/ReferAndEarn";

// SuperAdmin Components
import SuperAdminLayoutComponent from "./components/SuperAdmin/SuperAdminLayout";

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      <p className="mt-4 text-gray-500">Loading...</p>
    </div>
  </div>
);

// Role-based Route Component
const RoleBasedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Return the children - routing is handled by AppRoutes
  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/app/" replace />;
  }

  return children;
};

// SuperAdmin Route Component
const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSuperAdmin();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/superadmin/login" replace />;
  }

  return children;
};

// Channel Partner Route Component
const PartnerRoute = ({ children }) => {
  const { isAuthenticated, loading } = useChannelPartnerAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/partner/login" replace />;
  }

  return children;
};

// SuperAdmin Layout - using imported component
const SuperAdminLayout = SuperAdminLayoutComponent;

// Admin Routes
const AdminRoutes = () => (
  <Routes>
    <Route path="/" element={<AdminDashboard />} />
    <Route path="/analytics" element={<Analytics />} />
    {/* CEO Dashboard route kept (page exists; its sidebar item is commented out) */}
    <Route path="/ceo-dashboard" element={<CEODashboard />} />
    <Route path="/leads" element={<LeadsList />} />
    {/* More specific lead routes must come before /leads/:id, which would otherwise swallow them */}
    <Route path="/leads/segments" element={<SmartLists />} />
    <Route path="/leads/re-engagement" element={<ReEngagement />} />
    <Route path="/leads/territories" element={<Territories />} />
    {/* Auto-Assign Rules lives inside the Leads section as a tab */}
    <Route
      path="/leads/assignment-rules"
      element={
        <Layout>
          <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
            <LeadsTabs />
            <AssignmentRules />
          </div>
        </Layout>
      }
    />
    <Route path="/leads/:id" element={<LeadDetails />} />
    <Route path="/leads/assign" element={<AssignLeads />} />
    <Route path="/users" element={<UsersList />} />
    <Route path="/users/:id" element={<UserDetails />} />
    <Route path="/call-logs" element={<CallLogs />} />
    <Route path="/recordings" element={<RecordingsList />} />
    <Route path="/call-analysis" element={<CallAnalysis />} />
    <Route path="/call-analysis/:id" element={<CallAnalysisDetail />} />
    <Route path="/reports" element={<Reports />} />
    <Route path="/reports/daily-work" element={<DailyWorkReports />} />
    <Route path="/leaderboard" element={<Leaderboard />} />
    <Route path="/attendance" element={<Attendance />} />
    <Route path="/settings" element={<Settings />} />
    <Route path="/settings/:tab" element={<Settings />} />
    <Route path="/audit-logs" element={<AuditLogs />} />
    <Route path="/support" element={<SupportPage />} />
    <Route path="/checkout" element={<Checkout />} />
    <Route path="/refer-and-earn" element={<ReferAndEarn />} />
    <Route path="/products" element={<ProductsList />} />
    <Route path="/scripts" element={<ScriptsList />} />
    <Route path="/follow-ups" element={<FollowUpsList />} />
    <Route path="/visits" element={<VisitsList />} />
    <Route path="/escalations" element={<EscalatedFollowUps />} />
    <Route path="*" element={<Navigate to="/app/" replace />} />
  </Routes>
);

// User/Telecaller Routes
const UserRoutes = () => (
  <Routes>
    <Route path="/" element={<UserDashboard />} />
    <Route path="/my-reports" element={<UserReports />} />
    {/* Same Leads page as admins, scoped to the telecaller's assigned leads */}
    <Route path="/leads" element={<LeadsList />} />
    <Route path="/leads/:id" element={<LeadDetails />} />
    <Route path="/call-logs" element={<UserCallLogs />} />
    <Route path="/visits" element={<VisitsList />} />
    <Route path="*" element={<Navigate to="/app/" replace />} />
  </Routes>
);

// App Routes based on role
const AppRoutes = () => {
  const { isAdmin, isTelecaller, isAuthenticated } = useAuth();
  const { isExpired, loading } = useOrg();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // While org data is loading, show a spinner instead of the dashboard.
  // This prevents a brief flash of the admin panel before the expired modal appears.
  if (loading) {
    return <LoadingSpinner />;
  }

  // Show subscription expired modal if subscription has expired or trial has expired
  if (isExpired) {
    return <SubscriptionExpiredModal />;
  }

  // Show Admin Panel for admin users
  if (isAdmin) {
    return <AdminRoutes />;
  }

  // Show User Panel for telecaller users
  if (isTelecaller) {
    return <UserRoutes />;
  }

  // Default fallback - redirect to login
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Login />} />
            {/* Customer ticket status check (no login required) */}
            <Route path="/track-ticket" element={<TrackTicket />} />
            {/* <Route path="/plans" element={<LandingPage />} /> */}
            {/* <Route path="/privacy" element={<Privacy />} /> */}
            {/* <Route path="/terms" element={<Terms />} /> */}

            {/* Login Route */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />

            {/* Register Route */}
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />

            {/* Auth Callback — cross-app auto-login from landing page */}
            <Route
              path="/auth/callback"
              element={<AuthCallback />}
            />

            {/* Referral link landing — see ReferralRedirect.jsx for why this
                isn't just /register?ref=... */}
            <Route path="/r/:code" element={<ReferralRedirect />} />

            <Route
              path="/app/*"
              element={
                <RoleBasedRoute>
                  <OrgProvider>
                    <AppRoutes />
                  </OrgProvider>
                </RoleBasedRoute>
              }
            />
            {/* Backward compatibility - redirect /dashboard etc to /app/ */}
            <Route
              path="/dashboard"
              element={<Navigate to="/app/" replace />}
            />
            <Route
              path="/leads"
              element={<Navigate to="/app/leads" replace />}
            />
            <Route
              path="/users"
              element={<Navigate to="/app/users" replace />}
            />
            <Route
              path="/call-logs"
              element={<Navigate to="/app/call-logs" replace />}
            />
            <Route
              path="/reports"
              element={<Navigate to="/app/reports" replace />}
            />
            <Route
              path="/attendance"
              element={<Navigate to="/app/attendance" replace />}
            />
            <Route
              path="/settings"
              element={<Navigate to="/app/settings" replace />}
            />

            {/* SuperAdmin Routes */}
            <Route
              path="/superadmin/login"
              element={
                <SuperAdminProvider>
                  <SuperAdminLogin />
                </SuperAdminProvider>
              }
            />
            <Route
              path="/superadmin"
              element={
                <SuperAdminProvider>
                  <SuperAdminRoute>
                    <SuperAdminLayout />
                  </SuperAdminRoute>
                </SuperAdminProvider>
              }
            >
              <Route path="dashboard" element={<SuperAdminDashboard />} />
              <Route path="organizations" element={<OrganizationsList />} />
              <Route path="plans" element={<PlansList />} />
              <Route path="promocodes" element={<PromocodesList />} />
              <Route path="channel-partners" element={<ChannelPartnersList />} />
              <Route path="leads" element={<SuperAdminLeadsList />} />
              <Route path="demo-bookings" element={<DemoBookingsList />} />
              <Route path="notices" element={<NoticesList />} />
              <Route path="app-versions" element={<AppVersions />} />
              <Route path="invoice-settings" element={<InvoiceSettings />} />
              <Route path="settings" element={<SuperAdminSettings />} />
              <Route path="support" element={<SupportTickets />} />
              <Route path="partner-tickets" element={<PartnerTickets />} />
              <Route path="blog" element={<BlogPosts />} />
              <Route path="videos" element={<Videos />} />
              <Route path="other-products" element={<OtherProducts />} />
              <Route path="renewal-report" element={<RenewalPerformanceReport />} />
              <Route path="renewal-conversion" element={<RenewalConversionAnalytics />} />
              <Route path="renewal-agent-performance" element={<RenewalAgentPerformance />} />
              <Route path="renewal-loss" element={<RenewalLossReport />} />
              <Route path="subscription-expiry" element={<SubscriptionExpiryReport />} />
              <Route path="ticket-resolution" element={<TicketResolutionAnalysis />} />
              <Route path="first-response" element={<FirstResponseTimeAnalysis />} />
              <Route path="sla-performance" element={<SlaPerformanceReport />} />
              <Route path="overdue-tickets" element={<OverdueTicketReport />} />
              <Route path="escalations" element={<EscalationReport />} />
              <Route path="reopened-tickets" element={<ReopenedTicketReport />} />
              <Route path="customer-tickets" element={<CustomerWiseTicketReport />} />
              <Route path="product-tickets" element={<ProductWiseTicketReport />} />
              <Route path="ticket-trends" element={<TicketTrendAnalysis />} />
              <Route path="ticket-closure" element={<TicketClosureRate />} />
              <Route path="admins" element={<AdminsList />} />
              <Route path="audit-logs" element={<SuperAdminAuditLogs />} />
              <Route
                path=""
                element={<Navigate to="/superadmin/dashboard" replace />}
              />
            </Route>

            {/* Channel Partner Routes */}
            <Route
              path="/partner/login"
              element={
                <ChannelPartnerAuthProvider>
                  <PartnerLogin />
                </ChannelPartnerAuthProvider>
              }
            />
            <Route
              path="/partner"
              element={
                <ChannelPartnerAuthProvider>
                  <PartnerRoute>
                    <PartnerLayout />
                  </PartnerRoute>
                </ChannelPartnerAuthProvider>
              }
            >
              <Route path="dashboard" element={<PartnerDashboard />} />
              <Route path="organizations" element={<PartnerOrganizations />} />
              <Route path="pipeline" element={<PartnerPipeline />} />
              <Route path="schedule-meeting" element={<PartnerScheduleMeeting />} />
              <Route path="support" element={<PartnerSupport />} />
              <Route path="help" element={<PartnerHelp />} />
              <Route path="settings" element={<PartnerSettings />} />
              <Route path="" element={<Navigate to="/partner/dashboard" replace />} />
            </Route>

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
      <Toaster
        position="top-center"
        containerStyle={{ zIndex: 999999 }}
        toastOptions={{
          duration: 3000,
          style: {
            background: "#333",
            color: "#fff",
            borderRadius: "8px",
          },
          success: {
            iconTheme: { primary: "#10B981", secondary: "#fff" },
          },
          error: {
            iconTheme: { primary: "#EF4444", secondary: "#fff" },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
